import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api, setActiveBusinessId } from '../lib/api.ts';
import type { BusinessSummary, User } from '../lib/types.ts';

interface SessionResponse {
  user: User;
  businesses: BusinessSummary[];
  activeBusinessId: string | null;
}

interface SessionValue {
  status: 'loading' | 'signed-in' | 'signed-out';
  user: User | null;
  businesses: BusinessSummary[];
  business: BusinessSummary | null;
  role: BusinessSummary['role'] | null;
  /** True when the current role may change data. */
  canWrite: boolean;
  canAdmin: boolean;
  switchBusiness: (id: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (input: SignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface SignUpInput {
  email: string;
  password: string;
  name: string;
  /** Omitted when joining an existing company through an invitation. */
  businessName?: string;
  taxRegime?: string;
  /** Chosen while signing up. Recorded as intent, never granted. */
  plan?: string;
  /** The price list they were quoted against, so it is the one stored. */
  priceRegion?: string;
  inviteToken?: string;
}

const SessionContext = createContext<SessionValue | null>(null);

const STORAGE_KEY = 'vela:active-business';

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SessionValue['status']>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  /**
   * Adopt a session payload. The remembered business only survives if the
   * user is still a member of it — otherwise a stale id would 403 every call.
   */
  const adopt = useCallback((session: SessionResponse) => {
    setUser(session.user);
    setBusinesses(session.businesses);

    const remembered = localStorage.getItem(STORAGE_KEY);
    const stillAMember = session.businesses.some((business) => business.id === remembered);
    const next = stillAMember ? remembered : (session.activeBusinessId ?? session.businesses[0]?.id ?? null);

    setActiveId(next);
    setActiveBusinessId(next);
    if (next) localStorage.setItem(STORAGE_KEY, next);
    setStatus('signed-in');
  }, []);

  const clear = useCallback(() => {
    setUser(null);
    setBusinesses([]);
    setActiveId(null);
    setActiveBusinessId(null);
    setStatus('signed-out');
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async () => {
    try {
      adopt(await api.unscoped.get<SessionResponse>('/api/auth/me'));
    } catch {
      clear();
    }
  }, [adopt, clear]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const switchBusiness = useCallback(
    (id: string) => {
      setActiveId(id);
      setActiveBusinessId(id);
      localStorage.setItem(STORAGE_KEY, id);
      // Every cached query belongs to the business it was fetched under.
      queryClient.clear();
    },
    [queryClient],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      adopt(await api.unscoped.post<SessionResponse>('/api/auth/login', { email, password }));
    },
    [adopt],
  );

  const signUp = useCallback(
    async (input: SignUpInput) => {
      adopt(await api.unscoped.post<SessionResponse>('/api/auth/signup', input));
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    try {
      await api.unscoped.post('/api/auth/logout');
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      clear();
    }
  }, [clear]);

  const value = useMemo<SessionValue>(() => {
    const business = businesses.find((entry) => entry.id === activeId) ?? null;
    const role = business?.role ?? null;
    return {
      status,
      user,
      businesses,
      business,
      role,
      canWrite: role === 'owner' || role === 'admin' || role === 'accountant',
      canAdmin: role === 'owner' || role === 'admin',
      switchBusiness,
      signIn,
      signUp,
      signOut,
      refresh,
    };
  }, [status, user, businesses, activeId, switchBusiness, signIn, signUp, signOut, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
